/**
 * Chart documentation shown behind the info icon on the Resources dashboard.
 *
 * Keys match the chart link property names in `MopaResourcesComponent`.
 */
export const MopaResourcesChartDocs = {
    // ENERGY

    energy_inflow: /* HTML */ `<p>
            Total energy granted on each day.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - energy granted.
        </p>
        <p>
            <strong>What's counted:</strong> tracked units added to player
            balances; CloudSaveSynchronization events are excluded. A plain sum
            also reflects active-base size.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>; valid players only.
        </p>`,

    avg_energy_inflow_per_active_user: /* HTML */ `<p>
            Energy granted per active user on each day.
        </p>
        <pre>
  total energy inflow (numerator)
  ------------------------------
  DAU that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, daily; Y - energy granted per
            active user.
        </p>
        <p>
            The denominator is that day's active-user count, not only the
            players who moved the resource.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (DAU); valid players
            only.
        </p>`,

    running_avg_energy_inflow_per_user_by_day_no: /* HTML */ `<p>
            Cumulative energy granted per user in the selected cohorts by day
            number.
        </p>
        <pre>
  cohort energy inflow at day N (numerator)
  ------------------------------
  day-0 cohort size (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - cumulative energy granted per user.
        </p>
        <p>
            Fixed denominator includes churned and immature users; capped at day
            90.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    energy_outflow: /* HTML */ `<p>
            Total energy spent on each day.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - energy spent.
        </p>
        <p>
            <strong>What's counted:</strong> tracked units removed from player
            balances; CloudSaveSynchronization events are excluded. A plain sum
            also reflects active-base size.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>; valid players only.
        </p>`,

    avg_energy_outflow_per_active_user: /* HTML */ `<p>
            Energy spent per active user on each day.
        </p>
        <pre>
  total energy outflow (numerator)
  ------------------------------
  DAU that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, daily; Y - energy spent per
            active user.
        </p>
        <p>
            The denominator is that day's active-user count, not only the
            players who moved the resource.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (DAU); valid players
            only.
        </p>`,

    running_avg_energy_outflow_per_user_by_day_no: /* HTML */ `<p>
            Cumulative energy spent per user in the selected cohorts by day
            number.
        </p>
        <pre>
  cohort energy outflow at day N (numerator)
  ------------------------------
  day-0 cohort size (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - cumulative energy spent per user.
        </p>
        <p>
            Fixed denominator includes churned and immature users; capped at day
            90.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    energy_balance: /* HTML */ `<p>
            Net energy change on each day.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - energy granted minus
            spent, so below zero more was spent than granted.
        </p>
        <p>
            <strong>What's counted:</strong> inflow minus outflow; positive
            means net grants. No movement is NULL.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>; valid players only.
        </p>`,

    avg_energy_balance_per_active_user: /* HTML */ `<p>
            Net energy change per active user on each day.
        </p>
        <pre>
  net energy change (numerator)
  ------------------------------
  DAU that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, daily; Y - net energy change
            per active user.
        </p>
        <p>
            The denominator is that day's active-user count, not only the
            players who moved the resource.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (DAU); valid players
            only.
        </p>`,

    running_avg_energy_balance_per_user_by_day_no: /* HTML */ `<p>
            Cumulative net energy change per user in the selected cohorts by day
            number.
        </p>
        <pre>
  cohort net energy change at day N (numerator)
  ------------------------------
  day-0 cohort size (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - cumulative net energy change per user.
        </p>
        <p>
            Fixed denominator includes churned and immature users; capped at day
            90.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    // GEMS

    gems_inflow: /* HTML */ `<p>
            Total gems granted on each day.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - gems granted.
        </p>
        <p>
            <strong>What's counted:</strong> tracked units added to player
            balances; CloudSaveSynchronization events are excluded. A plain sum
            also reflects active-base size.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>; valid players only.
        </p>`,

    avg_gems_inflow_per_active_user: /* HTML */ `<p>
            Gems granted per active user on each day.
        </p>
        <pre>
  total gems inflow (numerator)
  ------------------------------
  DAU that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, daily; Y - gems granted per
            active user.
        </p>
        <p>
            The denominator is that day's active-user count, not only the
            players who moved the resource.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (DAU); valid players
            only.
        </p>`,

    running_avg_gems_inflow_per_user_by_day_no: /* HTML */ `<p>
            Cumulative gems granted per user in the selected cohorts by day
            number.
        </p>
        <pre>
  cohort gems inflow at day N (numerator)
  ------------------------------
  day-0 cohort size (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - cumulative gems granted per user.
        </p>
        <p>
            Fixed denominator includes churned and immature users; capped at day
            90.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    gems_outflow: /* HTML */ `<p>
            Total gems spent on each day.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - gems spent.
        </p>
        <p>
            <strong>What's counted:</strong> tracked units removed from player
            balances; CloudSaveSynchronization events are excluded. A plain sum
            also reflects active-base size.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>; valid players only.
        </p>`,

    avg_gems_outflow_per_active_user: /* HTML */ `<p>
            Gems spent per active user on each day.
        </p>
        <pre>
  total gems outflow (numerator)
  ------------------------------
  DAU that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, daily; Y - gems spent per
            active user.
        </p>
        <p>
            The denominator is that day's active-user count, not only the
            players who moved the resource.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (DAU); valid players
            only.
        </p>`,

    running_avg_gems_outflow_per_user_by_day_no: /* HTML */ `<p>
            Cumulative gems spent per user in the selected cohorts by day
            number.
        </p>
        <pre>
  cohort gems outflow at day N (numerator)
  ------------------------------
  day-0 cohort size (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - cumulative gems spent per user.
        </p>
        <p>
            Fixed denominator includes churned and immature users; capped at day
            90.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    gems_balance: /* HTML */ `<p>
            Net gems change on each day.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - gems granted minus
            spent, so below zero more was spent than granted.
        </p>
        <p>
            <strong>What's counted:</strong> inflow minus outflow; positive
            means net grants. No movement is NULL.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>; valid players only.
        </p>`,

    avg_gems_balance_per_active_user: /* HTML */ `<p>
            Net gems change per active user on each day.
        </p>
        <pre>
  net gems change (numerator)
  ------------------------------
  DAU that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, daily; Y - net gems change per
            active user.
        </p>
        <p>
            The denominator is that day's active-user count, not only the
            players who moved the resource.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (DAU); valid players
            only.
        </p>`,

    running_avg_gems_balance_per_user_by_day_no: /* HTML */ `<p>
            Cumulative net gems change per user in the selected cohorts by day
            number.
        </p>
        <pre>
  cohort net gems change at day N (numerator)
  ------------------------------
  day-0 cohort size (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - cumulative net gems change per user.
        </p>
        <p>
            Fixed denominator includes churned and immature users; capped at day
            90.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    // HINTS

    hints_inflow: /* HTML */ `<p>
            Total hints granted on each day.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - hints granted.
        </p>
        <p>
            <strong>What's counted:</strong> tracked units added to player
            balances; CloudSaveSynchronization events are excluded. A plain sum
            also reflects active-base size.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>; valid players only.
        </p>`,

    avg_hints_inflow_per_active_user: /* HTML */ `<p>
            Hints granted per active user on each day.
        </p>
        <pre>
  total hints inflow (numerator)
  ------------------------------
  DAU that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, daily; Y - hints granted per
            active user.
        </p>
        <p>
            The denominator is that day's active-user count, not only the
            players who moved the resource.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (DAU); valid players
            only.
        </p>`,

    running_avg_hints_inflow_per_user_by_day_no: /* HTML */ `<p>
            Cumulative hints granted per user in the selected cohorts by day
            number.
        </p>
        <pre>
  cohort hints inflow at day N (numerator)
  ------------------------------
  day-0 cohort size (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - cumulative hints granted per user.
        </p>
        <p>
            Fixed denominator includes churned and immature users; capped at day
            90.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    hints_outflow: /* HTML */ `<p>
            Total hints spent on each day.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - hints spent.
        </p>
        <p>
            <strong>What's counted:</strong> tracked units removed from player
            balances; CloudSaveSynchronization events are excluded. A plain sum
            also reflects active-base size.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>; valid players only.
        </p>`,

    avg_hints_outflow_per_active_user: /* HTML */ `<p>
            Hints spent per active user on each day.
        </p>
        <pre>
  total hints outflow (numerator)
  ------------------------------
  DAU that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, daily; Y - hints spent per
            active user.
        </p>
        <p>
            The denominator is that day's active-user count, not only the
            players who moved the resource.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (DAU); valid players
            only.
        </p>`,

    running_avg_hints_outflow_per_user_by_day_no: /* HTML */ `<p>
            Cumulative hints spent per user in the selected cohorts by day
            number.
        </p>
        <pre>
  cohort hints outflow at day N (numerator)
  ------------------------------
  day-0 cohort size (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - cumulative hints spent per user.
        </p>
        <p>
            Fixed denominator includes churned and immature users; capped at day
            90.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    hints_balance: /* HTML */ `<p>
            Net hints change on each day.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - hints granted minus
            spent, so below zero more was spent than granted.
        </p>
        <p>
            <strong>What's counted:</strong> inflow minus outflow; positive
            means net grants. No movement is NULL.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>; valid players only.
        </p>`,

    avg_hints_balance_per_active_user: /* HTML */ `<p>
            Net hints change per active user on each day.
        </p>
        <pre>
  net hints change (numerator)
  ------------------------------
  DAU that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, daily; Y - net hints change
            per active user.
        </p>
        <p>
            The denominator is that day's active-user count, not only the
            players who moved the resource.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (DAU); valid players
            only.
        </p>`,

    running_avg_hints_balance_per_user_by_day_no: /* HTML */ `<p>
            Cumulative net hints change per user in the selected cohorts by day
            number.
        </p>
        <pre>
  cohort net hints change at day N (numerator)
  ------------------------------
  day-0 cohort size (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - cumulative net hints change per user.
        </p>
        <p>
            Fixed denominator includes churned and immature users; capped at day
            90.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    // COINS

    coins_inflow: /* HTML */ `<p>
            Total coins granted on each day.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - coins granted.
        </p>
        <p>
            <strong>What's counted:</strong> tracked units added to player
            balances; CloudSaveSynchronization events are excluded. A plain sum
            also reflects active-base size.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>; valid players only.
        </p>`,

    avg_coins_inflow_per_active_user: /* HTML */ `<p>
            Coins granted per active user on each day.
        </p>
        <pre>
  total coins inflow (numerator)
  ------------------------------
  DAU that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, daily; Y - coins granted per
            active user.
        </p>
        <p>
            The denominator is that day's active-user count, not only the
            players who moved the resource.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (DAU); valid players
            only.
        </p>`,

    running_avg_coins_inflow_per_user_by_day_no: /* HTML */ `<p>
            Cumulative coins granted per user in the selected cohorts by day
            number.
        </p>
        <pre>
  cohort coins inflow at day N (numerator)
  ------------------------------
  day-0 cohort size (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - cumulative coins granted per user.
        </p>
        <p>
            Fixed denominator includes churned and immature users; capped at day
            90.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    coins_outflow: /* HTML */ `<p>
            Total coins spent on each day.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - coins spent.
        </p>
        <p>
            <strong>What's counted:</strong> tracked units removed from player
            balances; CloudSaveSynchronization events are excluded. A plain sum
            also reflects active-base size.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>; valid players only.
        </p>`,

    avg_coins_outflow_per_active_user: /* HTML */ `<p>
            Coins spent per active user on each day.
        </p>
        <pre>
  total coins outflow (numerator)
  ------------------------------
  DAU that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, daily; Y - coins spent per
            active user.
        </p>
        <p>
            The denominator is that day's active-user count, not only the
            players who moved the resource.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (DAU); valid players
            only.
        </p>`,

    running_avg_coins_outflow_per_user_by_day_no: /* HTML */ `<p>
            Cumulative coins spent per user in the selected cohorts by day
            number.
        </p>
        <pre>
  cohort coins outflow at day N (numerator)
  ------------------------------
  day-0 cohort size (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - cumulative coins spent per user.
        </p>
        <p>
            Fixed denominator includes churned and immature users; capped at day
            90.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    coins_balance: /* HTML */ `<p>
            Net coins change on each day.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - coins granted minus
            spent, so below zero more was spent than granted.
        </p>
        <p>
            <strong>What's counted:</strong> inflow minus outflow; positive
            means net grants. No movement is NULL.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>; valid players only.
        </p>`,

    avg_coins_balance_per_active_user: /* HTML */ `<p>
            Net coins change per active user on each day.
        </p>
        <pre>
  net coins change (numerator)
  ------------------------------
  DAU that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, daily; Y - net coins change
            per active user.
        </p>
        <p>
            The denominator is that day's active-user count, not only the
            players who moved the resource.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (DAU); valid players
            only.
        </p>`,

    running_avg_coins_balance_per_user_by_day_no: /* HTML */ `<p>
            Cumulative net coins change per user in the selected cohorts by day
            number.
        </p>
        <pre>
  cohort net coins change at day N (numerator)
  ------------------------------
  day-0 cohort size (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - cumulative net coins change per user.
        </p>
        <p>
            Fixed denominator includes churned and immature users; capped at day
            90.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    // COMPASSES

    compasses_inflow: /* HTML */ `<p>
            Total compasses granted on each day.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - compasses granted.
        </p>
        <p>
            <strong>What's counted:</strong> tracked units added to player
            balances; CloudSaveSynchronization events are excluded. A plain sum
            also reflects active-base size.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>; valid players only.
        </p>`,

    avg_compasses_inflow_per_active_user: /* HTML */ `<p>
            Compasses granted per active user on each day.
        </p>
        <pre>
  total compasses inflow (numerator)
  ------------------------------
  DAU that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, daily; Y - compasses granted
            per active user.
        </p>
        <p>
            The denominator is that day's active-user count, not only the
            players who moved the resource.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (DAU); valid players
            only.
        </p>`,

    running_avg_compasses_inflow_per_user_by_day_no: /* HTML */ `<p>
            Cumulative compasses granted per user in the selected cohorts by day
            number.
        </p>
        <pre>
  cohort compasses inflow at day N (numerator)
  ------------------------------
  day-0 cohort size (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - cumulative compasses granted per user.
        </p>
        <p>
            Fixed denominator includes churned and immature users; capped at day
            90.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    compasses_outflow: /* HTML */ `<p>
            Total compasses spent on each day.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - compasses spent.
        </p>
        <p>
            <strong>What's counted:</strong> tracked units removed from player
            balances; CloudSaveSynchronization events are excluded. A plain sum
            also reflects active-base size.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>; valid players only.
        </p>`,

    avg_compasses_outflow_per_active_user: /* HTML */ `<p>
            Compasses spent per active user on each day.
        </p>
        <pre>
  total compasses outflow (numerator)
  ------------------------------
  DAU that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, daily; Y - compasses spent per
            active user.
        </p>
        <p>
            The denominator is that day's active-user count, not only the
            players who moved the resource.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (DAU); valid players
            only.
        </p>`,

    running_avg_compasses_outflow_per_user_by_day_no: /* HTML */ `<p>
            Cumulative compasses spent per user in the selected cohorts by day
            number.
        </p>
        <pre>
  cohort compasses outflow at day N (numerator)
  ------------------------------
  day-0 cohort size (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - cumulative compasses spent per user.
        </p>
        <p>
            Fixed denominator includes churned and immature users; capped at day
            90.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    compasses_balance: /* HTML */ `<p>
            Net compasses change on each day.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - compasses granted minus
            spent, so below zero more was spent than granted.
        </p>
        <p>
            <strong>What's counted:</strong> inflow minus outflow; positive
            means net grants. No movement is NULL.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>; valid players only.
        </p>`,

    avg_compasses_balance_per_active_user: /* HTML */ `<p>
            Net compasses change per active user on each day.
        </p>
        <pre>
  net compasses change (numerator)
  ------------------------------
  DAU that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - date, daily; Y - net compasses change
            per active user.
        </p>
        <p>
            The denominator is that day's active-user count, not only the
            players who moved the resource.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (DAU); valid players
            only.
        </p>`,

    running_avg_compasses_balance_per_user_by_day_no: /* HTML */ `<p>
            Cumulative net compasses change per user in the selected cohorts by
            day number.
        </p>
        <pre>
  cohort net compasses change at day N (numerator)
  ------------------------------
  day-0 cohort size (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - cumulative net compasses change per user.
        </p>
        <p>
            Fixed denominator includes churned and immature users; capped at day
            90.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_resources</code>;
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,
} as const;
